import { useMyCompanyUser } from './use-my-company-user';
import { useCompanyContext } from '../providers';
import { isTeamAdmin, isTeamAdminOrGuardian } from '../util/team-roles';
import {
    isCompanyBilling,
    isCompanyOwner,
    isCompanyOwnerOrAdmin
} from '../util/company-roles';

type CompanyPermissions = {
    canViewGeneralSettings: boolean;
    canEditGeneralSettings: boolean;
    canViewBilling: boolean;
    canEditBilling: boolean;
    canViewTeams: boolean;
    canCreateTeams: boolean;
    canEditTeams: boolean;
    canViewMembers: boolean;
    canEditMembers: boolean;
    canEditOwners: boolean;
    canViewBeacons: boolean;
    canEditBeacons: boolean;
    canViewDevices: boolean;
    canEditDevices: boolean;
    canEditAnyDevice: boolean;
    canViewReports: boolean;
    canViewEmergencyBroadcast: boolean;
    canEditEmergencyBroadcast: boolean;
    hasSubscription: boolean;
    hasLicense: boolean;
    myUserId: string;
    myCompanyUserId: string;
};

const defaultPermissions: CompanyPermissions = {
    canViewGeneralSettings: false,
    canEditGeneralSettings: false,
    canViewBilling: false,
    canEditBilling: false,
    canViewTeams: false,
    canCreateTeams: false,
    canEditTeams: false,
    canViewMembers: false,
    canEditMembers: false,
    canEditOwners: false,
    canViewBeacons: false,
    canEditBeacons: false,
    canViewDevices: false,
    canEditDevices: false,
    canEditAnyDevice: false,
    canViewReports: false,
    canViewEmergencyBroadcast: false,
    canEditEmergencyBroadcast: false,
    hasSubscription: false,
    hasLicense: false,
    myUserId: '',
    myCompanyUserId: ''
};

export function useCompanyPermissions(): CompanyPermissions {
    const myCompanyUser = useMyCompanyUser();
    const { company } = useCompanyContext();

    if (!myCompanyUser) {
        return defaultPermissions;
    }

    const hasSubscription = company.subscription !== null;
    const hasLicense = myCompanyUser.license !== null;

    const isOwnerOrAdmin = isCompanyOwnerOrAdmin(myCompanyUser);
    // Holds a role in at least one team — team admins and guardians get some
    // company-wide access that plain members do not.
    const hasAnyTeamRole = myCompanyUser.teams.some(isTeamAdminOrGuardian);

    const canViewGeneralSettings = true;
    const canEditGeneralSettings = isOwnerOrAdmin;

    const canViewTeams = true;
    // const canViewTeams =
    //     isOwnerOrAdmin || myCompanyUser.teams.some(isTeamAdmin);
    const canCreateTeams = isOwnerOrAdmin;
    const canEditTeams = isOwnerOrAdmin;

    const canViewBilling = isOwnerOrAdmin || isCompanyBilling(myCompanyUser);
    const canEditBilling = isOwnerOrAdmin || isCompanyBilling(myCompanyUser);
    // Everyone can reach the members list. The server returns only the members
    // the caller may see, and what they can do to any one of them is decided
    // per member by that member's `capabilities`.
    const canViewMembers = true;
    const canEditMembers = isOwnerOrAdmin;
    const canEditOwners = isCompanyOwner(myCompanyUser);

    const canViewBeacons = isOwnerOrAdmin;
    const canEditBeacons = isOwnerOrAdmin;

    const canViewDevices = isOwnerOrAdmin;
    const canEditDevices = isOwnerOrAdmin;
    // Whether the user can edit a device *somewhere* — a superset of
    // `canEditDevices` (the main, company-wide list) that also covers team
    // admins, who can edit devices within their own team's Devices tab. Used to
    // decide whether to fetch authoritative subscription usage (that endpoint is
    // only callable by owner/admin/billing/team-admin).
    const canEditAnyDevice =
        canEditDevices || myCompanyUser.teams.some(isTeamAdmin);

    const canViewReports = isOwnerOrAdmin || hasAnyTeamRole;

    const canViewEmergencyBroadcast = isOwnerOrAdmin || hasAnyTeamRole;
    const canEditEmergencyBroadcast = isOwnerOrAdmin || hasAnyTeamRole;

    return {
        canViewGeneralSettings,
        canEditGeneralSettings,
        canViewBilling,
        canEditBilling,
        canViewTeams,
        canCreateTeams,
        canEditTeams,
        canViewMembers,
        canEditMembers,
        canEditOwners,
        canViewBeacons,
        canEditBeacons,
        canViewDevices,
        canEditDevices,
        canEditAnyDevice,
        canViewReports,
        canViewEmergencyBroadcast,
        canEditEmergencyBroadcast,
        hasSubscription,
        hasLicense,
        myUserId: myCompanyUser.userId ?? '',
        myCompanyUserId: myCompanyUser.id
    };
}
